import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Factory, RefreshCw, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../nativeinventory/NativeInventoryAdjustmentNew.css';

// Suggested Builds (Native) — every assembly below its BUILD POINT at a
// location, with a one-click draft work order per row. Position counts native
// on-hand + open NetSuite WOs + open native WOs; suggested qty builds back up
// to the preferred stock level (or the build point when it's higher), rounded
// to the item's reorder multiple. Creation goes through the existing native
// WO draft endpoint, idempotent per (day, sku) so double-clicks can't dupe.

const qty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

export default function NativeWoSuggestions() {
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(null);
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [qtyEdits, setQtyEdits] = useState({});
  const [creating, setCreating] = useState({});   // itemId -> true
  const [createdWo, setCreatedWo] = useState({}); // itemId -> wo number

  useEffect(() => {
    axios.get('/api/native-production/locations')
      .then(({ data }) => {
        const locs = data.locations || [];
        setLocations(locs);
        const rewined = locs.find((l) => l.code === 'REWINED') || locs[0];
        if (rewined) setLocation(rewined);
      })
      .catch(() => setLocations([]));
  }, []);

  const load = useCallback(async (loc) => {
    if (!loc) return;
    setLoading(true);
    setError(null);
    setRows(null);
    try {
      const { data } = await axios.get('/api/native-production/suggestions', { params: { locationId: loc.id } });
      setRows(data.suggestions || []);
      setMeta(data);
      setQtyEdits({});
      setCreatedWo({});
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(location); }, [location, load]);

  const effectiveQty = (r) => {
    const edit = qtyEdits[r.itemId];
    if (edit === undefined || edit === '') return r.suggestedQty;
    return Number(edit);
  };

  const createWo = async (r) => {
    const quantity = effectiveQty(r);
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    setCreating((c) => ({ ...c, [r.itemId]: true }));
    setError(null);
    try {
      const day = new Date().toISOString().slice(0, 10);
      const { data } = await axios.post('/api/native-production', {
        assemblyItemId: r.itemId,
        quantity,
        locationId: location.id,
        externalRef: `SUGG-WO-${day}-${r.sku}`,
        memo: `Suggested build: position ${qty(r.position)} below build point ${qty(r.buildPoint)}`,
      });
      const wo = data.workOrder || data;
      setCreatedWo((m) => ({ ...m, [r.itemId]: wo.wo_number || wo.woNumber || 'created' }));
    } catch (err) {
      setError(`${r.sku}: ${err.response?.data?.error || err.message}`);
    } finally {
      setCreating((c) => ({ ...c, [r.itemId]: false }));
    }
  };

  const createAll = async () => {
    for (const r of rows || []) {
      if (createdWo[r.itemId]) continue;
      // eslint-disable-next-line no-await-in-loop
      await createWo(r);
    }
  };

  const pending = (rows || []).filter((r) => !createdWo[r.itemId]).length;

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><Factory size={20} /> Suggested Builds (Native)</h1>
      </div>

      <div className="nia-card">
        <div className="nia-locations">
          {locations.map((l) => (
            <button key={l.id} className={`nia-location ${location?.id === l.id ? 'selected' : ''}`} onClick={() => setLocation(l)}>
              {l.name}
            </button>
          ))}
          <button className="nia-btn nia-btn-ghost" onClick={() => load(location)} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
        {meta && (
          <p className="nia-hint" style={{ marginTop: 8 }}>
            {meta.totalWithBuildPoints} assemblies carry a build point here · {meta.belowBuildPoint ?? (rows || []).length} below it.
            Position = native on hand + open NetSuite WOs + open native WOs. Suggested qty builds to the preferred stock
            level (rounded to the reorder multiple). Created WOs are native drafts — release them from the work-order screen.
          </p>
        )}
      </div>

      {loading && <div className="nia-card"><div className="nia-hint">Computing suggestions…</div></div>}
      {error && <div className="nia-error">{error}</div>}
      {meta?.note && <div className="nia-card"><div className="nia-hint">{meta.note}</div></div>}

      {rows && rows.length === 0 && !meta?.note && (
        <div className="nia-card">
          <div className="nia-hint">Nothing to build — every assembly with a build point is at or above it. </div>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="nia-card">
          <div className="nia-lines-header">
            <h3>{rows.length} assemblies below build point</h3>
            <button className="nia-btn nia-btn-primary" onClick={createAll} disabled={pending === 0 || Object.values(creating).some(Boolean)}>
              Create {pending} draft WO{pending === 1 ? '' : 's'}
            </button>
          </div>
          <table className="nia-table">
            <thead>
              <tr>
                <th>Assembly</th>
                <th className="r">On hand</th>
                <th className="r">Open WOs</th>
                <th className="r">Position</th>
                <th className="r">Build point</th>
                <th className="r">Target</th>
                <th className="r">Build qty</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.itemId}>
                  <td>
                    <Link to={`/native-items/${encodeURIComponent(r.sku)}`}><code>{r.sku}</code></Link> {r.name}
                  </td>
                  <td className="r">{qty(r.onHand)}</td>
                  <td className="r nia-sub">{qty(r.openNsWoQty + r.openNativeWoQty)}</td>
                  <td className="r"><strong>{qty(r.position)}</strong> <span className="nia-sub">({r.pctOfBuildPoint}%)</span></td>
                  <td className="r">{qty(r.buildPoint)}</td>
                  <td className="r nia-sub">{qty(r.target)}</td>
                  <td className="r">
                    <input
                      className="nia-input nia-input-num"
                      style={{ width: 76 }}
                      inputMode="decimal"
                      value={qtyEdits[r.itemId] ?? r.suggestedQty}
                      onChange={(e) => setQtyEdits((m) => ({ ...m, [r.itemId]: e.target.value }))}
                      disabled={!!createdWo[r.itemId]}
                    />
                  </td>
                  <td className="r">
                    {createdWo[r.itemId] ? (
                      <span className="nia-cost-source"><Check size={12} /> {createdWo[r.itemId]}</span>
                    ) : (
                      <button className="nia-btn nia-btn-mini" onClick={() => createWo(r)} disabled={!!creating[r.itemId]}>
                        {creating[r.itemId] ? '…' : 'Create WO'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
